import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
import 'dayjs/locale/pt-br.js';
import { consultationService } from '../services/consultationService.js';
import LoadingModal from './LoadingModal.js';
import './Dashboard.css';
import './Consultations.css';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('pt-br');

const RETURN_PRIORITY_LABELS = {
    normal: 'Normal',
    urgent: 'Urgente',
    emergency: 'Emergência',
};

const EXAM_LABELS = {
    parasitologico: 'Parasitológico',
    urinanalise: 'Urinálise',
    ultrassom: 'Ultrassom',
    raio_x: 'Raio-X',
    bioquimico: 'Bioquímico',
    hemograma: 'Hemograma',
};

const CONDITION_LABELS = {
    poor: 'Ruim',
    fair: 'Regular',
    good: 'Bom',
    excellent: 'Excelente',
};

const HYDRATION_LABELS = {
    dehydrated: 'Desidratado',
    mild: 'Leve',
    moderate: 'Moderada',
    good: 'Boa',
    normal: 'Normal',
};

function Consultations() {
    const [consultations, setConsultations] = useState([]);
    const [filteredConsultations, setFilteredConsultations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [expandedId, setExpandedId] = useState(null);
    const navigate = useNavigate();

    const toggleExpand = (id) => setExpandedId(prev => (prev === id ? null : id));

    const hasVitals = (c) =>
        c.weight || c.temperature || c.heartRate || c.respiratoryRate || c.generalCondition || c.hydrationLevel;

    const isIncomplete = (c) => c.active === false;

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        filterConsultations();
    }, [consultations, selectedFilter, searchTerm]);

    const loadData = async () => {
        try {
            setLoading(true);
            const result = await consultationService.getConsultations();
            if (result.success) {
                const sorted = [...result.consultations].sort(
                    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                );
                setConsultations(sorted);
            } else {
                toast.error(result.error || 'Erro ao carregar atendimentos');
            }
        } catch (err) {
            toast.error('Erro ao carregar atendimentos');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const filterConsultations = () => {
        let filtered = [...consultations];

        if (selectedFilter === 'with_exams') {
            filtered = filtered.filter(c => c.requestedExams && c.requestedExams.length > 0);
        } else if (selectedFilter === 'needs_return') {
            filtered = filtered.filter(c => c.needsReturn === 'yes');
        } else if (selectedFilter === 'with_diagnosis') {
            filtered = filtered.filter(c => c.clinicalDiagnosis);
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(c =>
                c.chiefComplaint?.toLowerCase().includes(term) ||
                c.clinicalDiagnosis?.toLowerCase().includes(term) ||
                c.vetName?.toLowerCase().includes(term) ||
                c.generalGuidelines?.toLowerCase().includes(term)
            );
        }

        setFilteredConsultations(filtered);
    };

    const formatDateTime = (dateString) => {
        return dayjs.utc(dateString).tz('America/Sao_Paulo').format('DD/MM/YYYY [às] HH:mm');
    };

    const getStats = () => ({
        total: consultations.length,
        withExams: consultations.filter(c => c.requestedExams && c.requestedExams.length > 0).length,
        needsReturn: consultations.filter(c => c.needsReturn === 'yes').length,
        withDiagnosis: consultations.filter(c => c.clinicalDiagnosis).length,
    });

    const getReturnPriorityClass = (priority) => {
        const map = { normal: 'priority-normal', urgent: 'priority-urgent', emergency: 'priority-emergency' };
        return map[priority] || 'priority-normal';
    };

    const stats = getStats();

    if (loading) {
        return (
            <div className="schedules-loading">
                <div className="loading-spinner"></div>
                <p>Carregando atendimentos...</p>
            </div>
        );
    }

    return (
        <>
            <LoadingModal isOpen={!!loadingMessage} message={loadingMessage} />

            {/* Header */}
            <div className="schedules-header-section">
                <div>
                    <h1 className="schedules-title">Atendimentos</h1>
                    <p className="schedules-subtitle">Histórico de atendimentos dos seus pacientes</p>
                </div>
            </div>

            {/* Stats */}
            <div className="schedules-stats">
                <div className="stat-card-small">
                    <div className="stat-icon-small" style={{ background: 'rgba(113, 101, 255, 0.1)' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-main)" strokeWidth="2">
                            <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
                            <rect x="9" y="3" width="6" height="4" rx="1" ry="1" />
                        </svg>
                    </div>
                    <div>
                        <p className="stat-label-small">Total</p>
                        <h3 className="stat-value-small">{stats.total}</h3>
                    </div>
                </div>

                <div className="stat-card-small">
                    <div className="stat-icon-small" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                    </div>
                    <div>
                        <p className="stat-label-small">Com Diagnóstico</p>
                        <h3 className="stat-value-small">{stats.withDiagnosis}</h3>
                    </div>
                </div>

                <div className="stat-card-small">
                    <div className="stat-icon-small" style={{ background: 'rgba(245, 158, 11, 0.1)' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning)" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                        </svg>
                    </div>
                    <div>
                        <p className="stat-label-small">Com Exames</p>
                        <h3 className="stat-value-small">{stats.withExams}</h3>
                    </div>
                </div>

                <div className="stat-card-small">
                    <div className="stat-icon-small" style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-error)" strokeWidth="2">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                            <polyline points="9 22 9 12 15 12 15 22" />
                        </svg>
                    </div>
                    <div>
                        <p className="stat-label-small">Retorno Indicado</p>
                        <h3 className="stat-value-small">{stats.needsReturn}</h3>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="schedules-filters">
                <div className="search-box">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Buscar por queixa, diagnóstico..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="status-filters">
                    <button className={`filter-btn ${selectedFilter === 'all' ? 'active' : ''}`} onClick={() => setSelectedFilter('all')}>
                        Todas
                    </button>
                    <button className={`filter-btn ${selectedFilter === 'with_diagnosis' ? 'active' : ''}`} onClick={() => setSelectedFilter('with_diagnosis')}>
                        Com Diagnóstico
                    </button>
                    <button className={`filter-btn ${selectedFilter === 'with_exams' ? 'active' : ''}`} onClick={() => setSelectedFilter('with_exams')}>
                        Com Exames
                    </button>
                    <button className={`filter-btn ${selectedFilter === 'needs_return' ? 'active' : ''}`} onClick={() => setSelectedFilter('needs_return')}>
                        Retorno
                    </button>
                </div>
            </div>

            {/* List */}
            <div className="schedules-list">
                {filteredConsultations.length === 0 ? (
                    <div className="empty-state">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
                            <rect x="9" y="3" width="6" height="4" rx="1" ry="1" />
                            <line x1="9" y1="12" x2="15" y2="12" />
                            <line x1="9" y1="16" x2="12" y2="16" />
                        </svg>
                        <p className="empty-text">Nenhuma consulta encontrada</p>
                        <p className="empty-subtext">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Os atendimentos realizados aparecerão aqui'}
                        </p>
                    </div>
                ) : (
                    filteredConsultations.map(consultation => {
                        const expanded = expandedId === consultation.id;
                        const incomplete = isIncomplete(consultation);
                        const vitals = hasVitals(consultation);
                        const hasExams = consultation.requestedExams && consultation.requestedExams.length > 0;
                        return (
                            <div key={consultation.id} className={`consultation-card${expanded ? ' expanded' : ''}`}>
                                {/* Card Header */}
                                <div
                                    className="consultation-card-header"
                                    onClick={() => toggleExpand(consultation.id)}
                                >
                                    <div className="consultation-meta">
                                        <div className="consultation-id-badge">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
                                                <rect x="9" y="3" width="6" height="4" rx="1" ry="1" />
                                            </svg>
                                            Consulta #{consultation.id}
                                        </div>
                                        <span className="consultation-date">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                <line x1="16" y1="2" x2="16" y2="6" />
                                                <line x1="8" y1="2" x2="8" y2="6" />
                                                <line x1="3" y1="10" x2="21" y2="10" />
                                            </svg>
                                            {formatDateTime(consultation.createdAt)}
                                        </span>
                                    </div>

                                    <div className="consultation-header-right">
                                        {/* Indicator badges */}
                                        <div className="consultation-indicators">
                                            {incomplete && (
                                                <span className="indicator-badge badge-draft">Rascunho</span>
                                            )}
                                            {vitals && (
                                                <span className="indicator-badge badge-vitals">
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
                                                    Sinais
                                                </span>
                                            )}
                                            {hasExams && (
                                                <span className="indicator-badge badge-exams">
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                                                    {consultation.requestedExams.length} Exame{consultation.requestedExams.length > 1 ? 's' : ''}
                                                </span>
                                            )}
                                            {consultation.clinicalDiagnosis && (
                                                <span className="indicator-badge badge-diagnosis">
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><polyline points="12 8 12 12 14 14" /></svg>
                                                    Diagnóstico
                                                </span>
                                            )}
                                            {consultation.needsReturn === 'yes' && (
                                                <span className={`return-badge ${getReturnPriorityClass(consultation.returnPriority)}`}>
                                                    Retorno {RETURN_PRIORITY_LABELS[consultation.returnPriority] || 'Normal'}
                                                </span>
                                            )}
                                        </div>

                                        <div className="consultation-header-actions">
                                            <button
                                                className="edit-btn-icon"
                                                title="Ver consulta"
                                                onClick={(e) => { e.stopPropagation(); navigate(`/appointment/${consultation.scheduleId}`); }}
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                    <circle cx="12" cy="12" r="3" />
                                                </svg>
                                            </button>

                                            <svg
                                                width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                                className={`expand-icon${expanded ? ' expanded' : ''}`}
                                            >
                                                <polyline points="6 9 12 15 18 9" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                {/* Chief complaint — always visible if present */}
                                {consultation.chiefComplaint && (
                                    <div className="consultation-summary">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                        </svg>
                                        <span>{consultation.chiefComplaint}</span>
                                    </div>
                                )}

                                {/* Expanded Details */}
                                {expanded && (
                                    <div className="consultation-details">

                                        {/* Vitals */}
                                        {vitals && (
                                            <div className="detail-section">
                                                <p className="detail-section-title">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
                                                    Sinais Vitais
                                                </p>
                                                <div className="vitals-grid">
                                                    {consultation.weight && (
                                                        <div className="vital-item">
                                                            <span className="vital-label">Peso</span>
                                                            <span className="vital-value">{consultation.weight} kg</span>
                                                        </div>
                                                    )}
                                                    {consultation.temperature && (
                                                        <div className="vital-item">
                                                            <span className="vital-label">Temperatura</span>
                                                            <span className="vital-value">{consultation.temperature} °C</span>
                                                        </div>
                                                    )}
                                                    {consultation.heartRate && (
                                                        <div className="vital-item">
                                                            <span className="vital-label">FC</span>
                                                            <span className="vital-value">{consultation.heartRate} bpm</span>
                                                        </div>
                                                    )}
                                                    {consultation.respiratoryRate && (
                                                        <div className="vital-item">
                                                            <span className="vital-label">FR</span>
                                                            <span className="vital-value">{consultation.respiratoryRate} rpm</span>
                                                        </div>
                                                    )}
                                                    {consultation.generalCondition && (
                                                        <div className="vital-item">
                                                            <span className="vital-label">Cond. Geral</span>
                                                            <span className={`vital-value vital-condition condition-${consultation.generalCondition}`}>
                                                                {CONDITION_LABELS[consultation.generalCondition] || consultation.generalCondition}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {consultation.hydrationLevel && (
                                                        <div className="vital-item">
                                                            <span className="vital-label">Hidratação</span>
                                                            <span className="vital-value">
                                                                {HYDRATION_LABELS[consultation.hydrationLevel] || consultation.hydrationLevel}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Clinical exam notes */}
                                        {consultation.physicalExamNotes && (
                                            <div className="detail-section">
                                                <p className="detail-section-title">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
                                                    Observações
                                                </p>
                                                <p className="detail-text">{consultation.physicalExamNotes}</p>
                                            </div>
                                        )}

                                        {/* History & observations */}
                                        {consultation.historyAndObservations && (
                                            <div className="detail-section">
                                                <p className="detail-section-title">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>
                                                    Histórico e Observações
                                                </p>
                                                <p className="detail-text">{consultation.historyAndObservations}</p>
                                            </div>
                                        )}

                                        {/* Clinical diagnosis */}
                                        {consultation.clinicalDiagnosis && (
                                            <div className="detail-section">
                                                <p className="detail-section-title">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                                                    Diagnóstico Clínico
                                                </p>
                                                <p className="detail-text">{consultation.clinicalDiagnosis}</p>
                                            </div>
                                        )}

                                        {/* Requested exams */}
                                        {hasExams && (
                                            <div className="detail-section">
                                                <p className="detail-section-title">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                                                    Exames Solicitados
                                                </p>
                                                <div className="exams-tags">
                                                    {consultation.requestedExams.map(exam => (
                                                        <span key={exam} className="exam-tag">
                                                            {EXAM_LABELS[exam] || exam}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Other exams */}
                                        {consultation.otherExams && (
                                            <div className="detail-section">
                                                <p className="detail-section-title">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                                                    Outros Exames
                                                </p>
                                                <p className="detail-text">{consultation.otherExams}</p>
                                            </div>
                                        )}

                                        {/* General guidelines */}
                                        {consultation.generalGuidelines && (
                                            <div className="detail-section">
                                                <p className="detail-section-title">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
                                                    Orientações Gerais
                                                </p>
                                                <p className="detail-text">{consultation.generalGuidelines}</p>
                                            </div>
                                        )}

                                        {/* Return info */}
                                        {consultation.needsReturn === 'yes' && (
                                            <div className="detail-section">
                                                <p className="detail-section-title">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
                                                    Retorno
                                                </p>
                                                <div className="return-info">
                                                    {consultation.returnDeadline && (
                                                        <div className="return-item">
                                                            <span className="return-label">Prazo</span>
                                                            <span className="return-value">{consultation.returnDeadline}</span>
                                                        </div>
                                                    )}
                                                    <div className="return-item">
                                                        <span className="return-label">Prioridade</span>
                                                        <span className={`return-value return-priority-text ${getReturnPriorityClass(consultation.returnPriority)}`}>
                                                            {RETURN_PRIORITY_LABELS[consultation.returnPriority] || 'Normal'}
                                                        </span>
                                                    </div>
                                                    {consultation.returnNotes && (
                                                        <div className="return-item return-item-full">
                                                            <span className="return-label">Observações</span>
                                                            <span className="return-value" style={{ fontWeight: 400 }}>{consultation.returnNotes}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Internal notes */}
                                        {consultation.internalNotes && (
                                            <div className="detail-section">
                                                <p className="detail-section-title">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" /></svg>
                                                    Notas Internas
                                                </p>
                                                <p className="detail-text detail-text-internal">{consultation.internalNotes}</p>
                                            </div>
                                        )}

                                        {/* Pet & schedule refs at bottom of card */}
                                        <div className="consultation-refs-footer">
                                            <span className="ref-tag">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="11" cy="4" r="2" /><circle cx="18" cy="8" r="2" /><circle cx="4" cy="8" r="2" /><ellipse cx="11" cy="16" rx="5" ry="4" />
                                                </svg>
                                                Pet #{consultation.petId}
                                            </span>
                                            <span className="ref-tag">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                                                </svg>
                                                Agendamento #{consultation.scheduleId}
                                            </span>
                                        </div>

                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </>
    );
}

export default Consultations;
